import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../api/api";
import { ERROR_MESSAGES } from "../../constants/enums";
import {
  IMirtapbxCallLog,
  IMirtapbxCallLogFilters,
} from "./mirtapbxCallLog.types";

// Params accepted by the call-log list fetch: a single record scope plus
// optional filters and pagination. Scope ids are mutually exclusive in practice
// (one of customer_id / customer_policy_id / lead_id) but all are optional here.
export interface IFetchCallLogsParams extends IMirtapbxCallLogFilters {
  customer_id?: number;
  customer_policy_id?: number;
  lead_id?: string;
  pageNo?: number;
  itemPerPage?: number;
}

export interface IFetchCallLogsResult {
  data: IMirtapbxCallLog[];
  total: number;
}

// Shape returned by the signed-recording endpoint.
export interface IRecordingUrlResult {
  id: number;
  signedUrl: string | null;
  expiresAt: string | null;
}

// Shape returned by the AI-analysis endpoint; data is null when not ready.
export interface ICallAiResult {
  id: number;
  data: IMirtapbxCallLog["ai_summary"];
}

export interface IManualLinkPayload {
  id: number;
  customer_id?: number;
  customer_policy_id?: number;
  lead_id?: string;
}

// Build a plain query-param object from scope + filters + pagination, omitting
// any undefined values so the request carries exactly what the caller provided.
export const buildCallLogParams = (
  params?: IFetchCallLogsParams,
): Record<string, string | number> => {
  const query: Record<string, string | number> = {};
  if (!params) return query;
  if (params.customer_id != null) query.customer_id = params.customer_id;
  if (params.customer_policy_id != null) query.customer_policy_id = params.customer_policy_id;
  if (params.lead_id != null) query.lead_id = params.lead_id;
  if (params.direction != null) query.direction = params.direction;
  if (params.disposition != null) query.disposition = params.disposition;
  if (params.link_method != null) query.link_method = params.link_method;
  if (params.user_fact_id != null) query.user_fact_id = params.user_fact_id;
  if (params.from_date != null) query.from_date = params.from_date;
  if (params.to_date != null) query.to_date = params.to_date;
  if (params.pageNo != null) query.pageNo = params.pageNo;
  if (params.itemPerPage != null) query.itemPerPage = params.itemPerPage;
  return query;
};

export const fetchCallLogsAsync = createAsyncThunk<
  IFetchCallLogsResult,
  IFetchCallLogsParams | void,
  { rejectValue: { message: string; status?: number } }
>("mirtapbxCallLog/fetchCallLogs", async (params, { rejectWithValue }) => {
  try {
    const res = await api.get("/mirtapbx/call-logs", {
      params: buildCallLogParams(params || undefined),
    });
    return {
      data: res.data.data,
      total: res.data.total ?? res.data.totalRecords ?? 0,
    };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchUnlinkedCallsAsync = createAsyncThunk<
  IFetchCallLogsResult,
  IFetchCallLogsParams | void,
  { rejectValue: { message: string; status?: number } }
>("mirtapbxCallLog/fetchUnlinkedCalls", async (params, { rejectWithValue }) => {
  try {
    const res = await api.get("/mirtapbx/call-logs/unlinked", {
      params: buildCallLogParams(params || undefined),
    });
    return {
      data: res.data.data,
      total: res.data.total ?? res.data.totalRecords ?? 0,
    };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

// Count-only poll used by the header badge. This hits the same unlinked
// endpoint but returns ONLY the total so it can never clobber the full page
// list in the store (the page and the badge share one slice).
export const fetchUnlinkedCountAsync = createAsyncThunk<
  { total: number },
  IFetchCallLogsParams | void,
  { rejectValue: { message: string; status?: number } }
>("mirtapbxCallLog/fetchUnlinkedCount", async (params, { rejectWithValue }) => {
  try {
    const res = await api.get("/mirtapbx/call-logs/unlinked", {
      params: buildCallLogParams(params || undefined),
    });
    return {
      total: res.data.total ?? res.data.totalRecords ?? 0,
    };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchRecordingUrlAsync = createAsyncThunk<
  IRecordingUrlResult,
  { id: number },
  { rejectValue: { message: string; status?: number } }
>("mirtapbxCallLog/fetchRecordingUrl", async ({ id }, { rejectWithValue }) => {
  try {
    const res = await api.get(`/mirtapbx/call-logs/${id}/recording`);
    const payload = res.data.data || res.data;
    return {
      id,
      signedUrl: payload?.signedUrl ?? null,
      expiresAt: payload?.expiresAt ?? null,
    };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchCallAiAsync = createAsyncThunk<
  ICallAiResult,
  { id: number },
  { rejectValue: { message: string; status?: number } }
>("mirtapbxCallLog/fetchCallAi", async ({ id }, { rejectWithValue }) => {
  try {
    const res = await api.get(`/mirtapbx/call-logs/${id}/ai-analysis`);
    // null = AI analysis not ready yet.
    return { id, data: res.data.data ?? null };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const manualLinkCallAsync = createAsyncThunk<
  { id: number; data: IMirtapbxCallLog | null },
  IManualLinkPayload,
  { rejectValue: { message: string; status?: number } }
>("mirtapbxCallLog/manualLinkCall", async (payload, { rejectWithValue }) => {
  try {
    const { id, ...body } = payload;
    const res = await api.patch(`/mirtapbx/call-logs/${id}/link`, body);
    return { id, data: res.data.data ?? null };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});
